import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { DndContext, DragEndEvent, closestCenter } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { X, Plus, Trash2, GripVertical, Loader2, Pin, PinOff, Search, Eye, EyeOff, Filter, Sparkles, Bot, Settings2, Calendar } from 'lucide-react';
import { boardsApi, mailApi, aiApi, SmartAiColumn } from '../../api/client';
import { toast } from 'sonner';
import FolderTree from './FolderTree';
import SmartColumnModal from './SmartColumnModal';
import { SmartColumnCreator } from '../../features/ai/components/SmartColumnCreator';
import type { FilterConfig } from '../../types/filter';

const EMOJI_OPTIONS = ['📬', '📥', '📤', '🗂️', '🏷️', '⭐', '🔥', '✅', '🚀', '💼', '📋', '🎯'];

interface Column {
  id: string;
  sourceType: string;
  sourceId: string;
  mailAccountId?: string;
  displayName: string;
  icon: string;
  frozen?: boolean;
  hidden?: boolean; // Punkt 4: Sichtbarkeit (komplett versteckt)
  hideWhenEmpty?: boolean; // Nur anzeigen wenn Einträge vorhanden
  filterConfig?: FilterConfig; // Smart column filter rules
  baseFolderId?: string | null; // Base folder for category columns (null = all)
}

interface Folder {
  id: string;
  name: string;
  displayName: string;
  type: 'folder' | 'label' | 'category';
  unreadCount?: number;
  totalCount?: number;
  children?: Folder[];
  color?: string;
}

interface Board {
  id: string;
  name: string;
  icon: string;
  isDefault: boolean;
  hideDuplicates?: boolean;
  showCompletionPopup?: boolean;
  columns: Column[];
  smartAiColumns?: SmartAiColumn[];
}

interface Account {
  id: string;
  provider: string;
  providerEmail: string;
}

interface BoardEditorProps {
  board: Board | null;
  accounts: Account[];
  onClose: () => void;
  onSave: () => void;
}

// Sortable Column Item Component
function SortableColumnItem({
  column,
  index,
  onToggleFrozen,
  onToggleHidden,
  onToggleHideWhenEmpty,
  onRemove,
  onChangeBaseFolder,
  folders,
}: {
  column: Column;
  index: number;
  onToggleFrozen: () => void;
  onToggleHidden: () => void;
  onToggleHideWhenEmpty: () => void;
  onRemove: () => void;
  onChangeBaseFolder?: (folderId: string | null) => void;
  folders?: Folder[];
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: column.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={{
        ...style,
        backgroundColor: column.hidden ? 'var(--bg-tertiary)' : 'var(--bg-secondary)',
        border: `1px solid ${column.frozen ? 'var(--brand-primary)' : 'var(--border-default)'}`,
      }}
      className="flex items-center gap-3 p-3 rounded-lg"
    >
      <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing">
        <GripVertical className="w-4 h-4" style={{ color: 'var(--text-disabled)' }} />
      </div>
      <span className="text-lg" style={{ opacity: column.hidden ? 0.5 : 1 }}>{column.icon}</span>
      <span 
        className="flex-1 text-sm" 
        style={{ 
          color: column.hidden ? 'var(--text-tertiary)' : 'var(--text-primary)',
          textDecoration: column.hidden ? 'line-through' : 'none',
        }}
      >
        {column.displayName}
      </span>
      <span 
        className="text-xs px-2 py-0.5 rounded flex items-center gap-1"
        style={{
          backgroundColor: column.sourceType === 'smart' 
            ? 'rgba(99, 102, 241, 0.15)' 
            : column.sourceType === 'meeting-prep'
            ? 'rgba(59, 130, 246, 0.15)'
            : 'var(--bg-tertiary)',
          color: column.sourceType === 'smart' 
            ? 'var(--brand-primary)' 
            : column.sourceType === 'meeting-prep'
            ? '#3b82f6'
            : 'var(--text-tertiary)',
        }}
      >
        {column.sourceType === 'smart' && <Sparkles className="w-3 h-3" />}
        {column.sourceType === 'meeting-prep' && <Calendar className="w-3 h-3" />}
        {column.sourceType === 'smart' ? 'smart' : column.sourceType === 'meeting-prep' ? 'meeting' : column.sourceType}
      </span>
      {/* Base Folder Selector for Category Columns */}
      {column.sourceType === 'category' && onChangeBaseFolder && folders && (
        <select
          value={column.baseFolderId || ''}
          onChange={(e) => onChangeBaseFolder(e.target.value || null)}
          className="text-xs px-2 py-1 rounded"
          style={{
            backgroundColor: 'var(--bg-tertiary)',
            border: '1px solid var(--border-default)',
            color: 'var(--text-primary)',
          }}
          title="Basis-Ordner (nur Mails aus diesem Ordner)"
        >
          <option value="">Alle Ordner</option>
          {folders
            .filter(f => f.type === 'folder')
            .map(f => (
              <option key={f.id} value={f.id}>{f.displayName}</option>
            ))
          }
        </select>
      )}
      {/* Hide When Empty Toggle */}
      <button
        onClick={onToggleHideWhenEmpty}
        className="p-1.5 rounded transition-colors"
        style={{ 
          color: column.hideWhenEmpty ? 'var(--brand-primary)' : 'var(--text-tertiary)',
          backgroundColor: column.hideWhenEmpty ? 'rgba(99, 102, 241, 0.1)' : 'transparent',
        }}
        title={column.hideWhenEmpty ? 'Immer anzeigen' : 'Nur anzeigen wenn Einträge vorhanden'}
        onMouseEnter={e => {
          if (!column.hideWhenEmpty) e.currentTarget.style.backgroundColor = 'var(--bg-hover)';
        }}
        onMouseLeave={e => {
          if (!column.hideWhenEmpty) e.currentTarget.style.backgroundColor = 'transparent';
        }}
      >
        <Filter className="w-4 h-4" />
      </button>
      {/* Visibility Toggle (Punkt 4) */}
      <button
        onClick={onToggleHidden}
        className="p-1.5 rounded transition-colors"
        style={{ 
          color: column.hidden ? 'var(--text-disabled)' : 'var(--text-tertiary)',
        }}
        title={column.hidden ? 'Spalte einblenden' : 'Spalte ausblenden (bei Zuweisungen verfügbar)'}
        onMouseEnter={e => e.currentTarget.style.backgroundColor = 'var(--bg-hover)'}
        onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
      >
        {column.hidden ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
      </button>
      {/* Freeze Toggle - only for first column */}
      {index === 0 && (
        <button
          onClick={onToggleFrozen}
          className="p-1.5 rounded transition-colors"
          style={{ 
            color: column.frozen ? 'var(--brand-primary)' : 'var(--text-tertiary)',
            backgroundColor: column.frozen ? 'rgba(99, 102, 241, 0.1)' : 'transparent',
          }}
          title={column.frozen ? 'Spalte entsperren' : 'Spalte fixieren (scrollt nicht mit)'}
          onMouseEnter={e => {
            if (!column.frozen) e.currentTarget.style.backgroundColor = 'var(--bg-hover)';
          }}
          onMouseLeave={e => {
            if (!column.frozen) e.currentTarget.style.backgroundColor = 'transparent';
          }}
        >
          {column.frozen ? <Pin className="w-4 h-4" /> : <PinOff className="w-4 h-4" />}
        </button>
      )}
      <button
        onClick={onRemove}
        className="p-1 rounded transition-colors"
        style={{ color: 'var(--text-tertiary)' }}
        onMouseEnter={e => {
          e.currentTarget.style.backgroundColor = 'var(--color-error-bg)';
          e.currentTarget.style.color = 'var(--color-error)';
        }}
        onMouseLeave={e => {
          e.currentTarget.style.backgroundColor = 'transparent';
          e.currentTarget.style.color = 'var(--text-tertiary)';
        }}
      >
        <Trash2 className="w-4 h-4" />
      </button>
    </div>
  );
}

export default function BoardEditor({ board, accounts, onClose, onSave }: BoardEditorProps) {
  const queryClient = useQueryClient();
  const isEditing = !!board;
  
  const [name, setName] = useState(board?.name || '');
  const [icon, setIcon] = useState(board?.icon || '📋');
  const [hideDuplicates, setHideDuplicates] = useState(board?.hideDuplicates || false);
  const [showCompletionPopup, setShowCompletionPopup] = useState(board?.showCompletionPopup ?? true);
  const [columns, setColumns] = useState<Column[]>(board?.columns || []);
  const [selectedAccount, setSelectedAccount] = useState<string | null>(
    accounts.length > 0 ? accounts[0].id : null
  );
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [folderSearch, setFolderSearch] = useState(''); // Punkt 2: Suchfeld
  const [showSmartColumnModal, setShowSmartColumnModal] = useState(false); // Smart Column Modal
  const [showAiColumnCreator, setShowAiColumnCreator] = useState(false); // AI Column Creator
  
  // Fetch folders for selected account
  const { data: foldersData, isLoading: foldersLoading } = useQuery({
    queryKey: ['folders', selectedAccount],
    queryFn: () => selectedAccount ? mailApi.getFolders(selectedAccount) : null,
    enabled: !!selectedAccount,
  });
  
  const folders = (foldersData?.folders || []) as Folder[];
  
  // Punkt 2: Filter folders by search
  const filteredFolders = useMemo(() => {
    if (!folderSearch.trim()) return folders;
    const search = folderSearch.toLowerCase();
    
    const filterRecursive = (items: Folder[]): Folder[] => {
      return items.reduce((acc: Folder[], folder) => {
        const matches = folder.displayName.toLowerCase().includes(search) ||
                       folder.name.toLowerCase().includes(search);
        const filteredChildren = folder.children ? filterRecursive(folder.children) : [];
        
        if (matches || filteredChildren.length > 0) {
          acc.push({
            ...folder,
            children: filteredChildren.length > 0 ? filteredChildren : folder.children,
          });
        }
        return acc;
      }, []);
    };
    
    return filterRecursive(folders);
  }, [folders, folderSearch]);
  
  // Punkt 3: Handle DnD column reorder
  const handleColumnDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    
    const oldIndex = columns.findIndex(c => c.id === active.id);
    const newIndex = columns.findIndex(c => c.id === over.id);
    
    if (oldIndex !== -1 && newIndex !== -1) {
      const newColumns = arrayMove(columns, oldIndex, newIndex);
      // Reset frozen if moved away from first position
      const updatedColumns = newColumns.map((col, i) => ({
        ...col,
        frozen: i === 0 ? col.frozen : false,
      }));
      setColumns(updatedColumns);
    }
  };
  
  // Punkt 4: Toggle visibility
  const handleToggleHidden = (index: number) => {
    setColumns(columns.map((col, i) => 
      i === index ? { ...col, hidden: !col.hidden } : col
    ));
  };
  
  // Toggle hide when empty
  const handleToggleHideWhenEmpty = (index: number) => {
    setColumns(columns.map((col, i) => 
      i === index ? { ...col, hideWhenEmpty: !col.hideWhenEmpty } : col
    ));
  };
  
  // Create/Update board mutation
  const saveMutation = useMutation({
    mutationFn: async () => {
      if (isEditing && board) {
        // Update name, icon, and settings
        await boardsApi.updateBoard(board.id, { name, icon, hideDuplicates, showCompletionPopup });
        
        // Sync columns: remove deleted, add new ones, update existing
        const existingColumnIds = board.columns.map((c: Column) => c.id);
        const currentColumnIds = columns.filter(c => !c.id.startsWith('temp-')).map(c => c.id);
        
        // Remove columns that were deleted
        for (const existingId of existingColumnIds) {
          if (!currentColumnIds.includes(existingId)) {
            await boardsApi.deleteColumn(existingId);
          }
        }
        
        // Update existing columns (frozen state, hidden state, baseFolderId, etc.)
        for (const col of columns) {
          if (!col.id.startsWith('temp-') && existingColumnIds.includes(col.id)) {
            const originalCol = board.columns.find((c: Column) => c.id === col.id);
            if (originalCol) {
              // Check if frozen, hidden, hideWhenEmpty, or baseFolderId changed
              const updates: { frozen?: boolean; hidden?: boolean; hideWhenEmpty?: boolean; baseFolderId?: string | null } = {};
              if (originalCol.frozen !== col.frozen) {
                updates.frozen = col.frozen;
              }
              if (originalCol.hidden !== col.hidden) {
                updates.hidden = col.hidden;
              }
              if (originalCol.hideWhenEmpty !== col.hideWhenEmpty) {
                updates.hideWhenEmpty = col.hideWhenEmpty;
              }
              if (originalCol.baseFolderId !== col.baseFolderId) {
                updates.baseFolderId = col.baseFolderId;
              }
              if (Object.keys(updates).length > 0) {
                await boardsApi.updateColumn(col.id, updates);
              }
            }
          }
        }
        
        // Add new columns (temp- prefixed IDs) and track new IDs for reordering
        const tempToRealId: Record<string, string> = {};
        for (const col of columns) {
          if (col.id.startsWith('temp-')) {
            const { column: newCol } = await boardsApi.addColumn(board.id, {
              sourceType: col.sourceType,
              sourceId: col.sourceId,
              mailAccountId: col.mailAccountId,
              displayName: col.displayName,
              icon: col.icon,
              frozen: col.frozen,
              filterConfig: col.filterConfig, // Smart column filter rules
              baseFolderId: col.baseFolderId, // Base folder for category columns
            });
            tempToRealId[col.id] = (newCol as { id: string }).id;
          }
        }
        
        // Reorder columns to match current order
        const orderedIds = columns.map(c => 
          c.id.startsWith('temp-') ? tempToRealId[c.id] : c.id
        ).filter(Boolean);
        
        if (orderedIds.length > 0) {
          await boardsApi.reorderColumns(board.id, orderedIds);
        }
      } else {
        const { board: newBoard } = await boardsApi.createBoard({ name, icon });
        const createdBoard = newBoard as { id: string };
        // Add columns in order (they get sorted by creation order initially)
        const newColumnIds: string[] = [];
        for (const col of columns) {
          const { column: newCol } = await boardsApi.addColumn(createdBoard.id, {
            sourceType: col.sourceType,
            sourceId: col.sourceId,
            mailAccountId: col.mailAccountId,
            displayName: col.displayName,
            icon: col.icon,
            frozen: col.frozen,
            filterConfig: col.filterConfig, // Smart column filter rules
            baseFolderId: col.baseFolderId, // Base folder for category columns
          });
          newColumnIds.push((newCol as { id: string }).id);
        }
        // Set correct order
        if (newColumnIds.length > 0) {
          await boardsApi.reorderColumns(createdBoard.id, newColumnIds);
        }
      }
    },
    onSuccess: () => {
      toast.success(isEditing ? 'Board aktualisiert' : 'Board erstellt');
      queryClient.invalidateQueries({ queryKey: ['boards'] });
      onSave();
    },
    onError: () => {
      toast.error('Fehler beim Speichern');
    },
  });
  
  // Delete board mutation
  const deleteMutation = useMutation({
    mutationFn: () => board ? boardsApi.deleteBoard(board.id) : Promise.resolve({ message: '' }),
    onSuccess: () => {
      toast.success('Board gelöscht');
      queryClient.invalidateQueries({ queryKey: ['boards'] });
      onSave();
    },
    onError: () => {
      toast.error('Fehler beim Löschen');
    },
  });
  
  const handleAddColumn = (folder: Folder) => {
    const account = accounts.find(a => a.id === selectedAccount);
    if (!account) return;
    
    // Check if already added (only check folder/category/label columns, NOT smart columns)
    // Smart columns use sourceId as base folder, but that shouldn't block adding the folder itself
    if (columns.some(c => c.sourceType !== 'smart' && c.sourceId === folder.id)) {
      toast.info(`${folder.displayName} ist bereits hinzugefügt`);
      return;
    }
    
    const getIcon = () => {
      if (folder.type === 'category') return '🏷️';
      if (folder.type === 'label') return '🔖';
      return '📁';
    };
    
    const newColumn: Column = {
      id: `temp-${Date.now()}`,
      sourceType: folder.type,
      sourceId: folder.id,
      mailAccountId: account.id,
      displayName: folder.displayName || folder.name,
      icon: getIcon(),
      frozen: false,
    };
    
    setColumns([...columns, newColumn]);
  };
  
  // Handle adding a smart column
  const handleAddSmartColumn = (data: {
    displayName: string;
    sourceType: 'smart';
    sourceId: string;
    mailAccountId: string;
    filterConfig: FilterConfig;
    icon?: string;
  }) => {
    const newColumn: Column = {
      id: `temp-${Date.now()}`,
      sourceType: 'smart',
      sourceId: data.sourceId,
      mailAccountId: data.mailAccountId,
      displayName: data.displayName,
      icon: data.icon || '🔍',
      frozen: false,
      filterConfig: data.filterConfig,
    };
    
    setColumns([...columns, newColumn]);
    setShowSmartColumnModal(false);
    toast.success(`Smart-Spalte "${data.displayName}" hinzugefügt`);
  };
  
  const handleRemoveColumn = (index: number) => {
    setColumns(columns.filter((_, i) => i !== index));
  };
  
  const handleToggleFrozen = (index: number) => {
    // Only first column can be frozen
    if (index !== 0) return;
    
    setColumns(columns.map((col, i) => 
      i === 0 ? { ...col, frozen: !col.frozen } : col
    ));
  };
  
  const handleSave = () => {
    if (!name.trim()) {
      toast.error('Name ist erforderlich');
      return;
    }
    saveMutation.mutate();
  };
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div 
        className="absolute inset-0" 
        style={{ backgroundColor: 'var(--bg-overlay)' }}
        onClick={onClose} 
      />
      
      <div 
        className="relative w-full max-w-2xl max-h-[90vh] rounded-2xl shadow-2xl overflow-hidden flex flex-col"
        style={{
          backgroundColor: 'var(--bg-primary)',
          border: '1px solid var(--border-default)',
        }}
      >
        {/* Header */}
        <div 
          className="flex items-center justify-between px-6 py-4"
          style={{ borderBottom: '1px solid var(--border-default)' }}
        >
          <h2 
            className="text-lg font-semibold"
            style={{ color: 'var(--text-primary)' }}
          >
            {isEditing ? 'Board bearbeiten' : 'Neues Board erstellen'}
          </h2>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg transition-colors"
            style={{ color: 'var(--text-tertiary)' }}
            onMouseEnter={e => e.currentTarget.style.backgroundColor = 'var(--bg-hover)'}
            onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Name & Icon */}
          <div className="flex gap-4">
            <div className="relative">
              <button
                onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                className="w-14 h-14 text-2xl rounded-lg transition-colors"
                style={{
                  backgroundColor: 'var(--bg-secondary)',
                  border: '1px solid var(--border-default)',
                }}
                onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--border-hover)'}
                onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border-default)'}
              >
                {icon}
              </button>
              {showEmojiPicker && (
                <div 
                  className="absolute top-full left-0 mt-1 p-2 rounded-lg shadow-xl z-10 grid grid-cols-6 gap-1"
                  style={{
                    backgroundColor: 'var(--bg-primary)',
                    border: '1px solid var(--border-default)',
                  }}
                >
                  {EMOJI_OPTIONS.map((emoji) => (
                    <button
                      key={emoji}
                      onClick={() => {
                        setIcon(emoji);
                        setShowEmojiPicker(false);
                      }}
                      className="w-8 h-8 text-lg rounded transition-colors"
                      onMouseEnter={e => e.currentTarget.style.backgroundColor = 'var(--bg-hover)'}
                      onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
              )}
            </div>
            
            <div className="flex-1">
              <label 
                className="block text-sm font-medium mb-1.5"
                style={{ color: 'var(--text-secondary)' }}
              >
                Board-Name
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-3 py-2.5 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-primary/40"
                style={{
                  backgroundColor: 'var(--bg-secondary)',
                  border: '1px solid var(--border-default)',
                  color: 'var(--text-primary)',
                }}
                placeholder="z.B. Projekte"
              />
            </div>
          </div>
          
          {/* Columns */}
          <div>
            <label 
              className="block text-sm font-medium mb-3"
              style={{ color: 'var(--text-secondary)' }}
            >
              Spalten
            </label>
            
            {/* Smart Column Button */}
            <button
              onClick={() => setShowSmartColumnModal(true)}
              className="flex items-center gap-2 w-full p-3 mb-2 rounded-lg transition-colors"
              style={{
                background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.1), rgba(139, 92, 246, 0.1))',
                border: '1px dashed var(--brand-primary)',
              }}
              onMouseEnter={e => e.currentTarget.style.backgroundColor = 'rgba(99, 102, 241, 0.15)'}
              onMouseLeave={e => e.currentTarget.style.backgroundColor = 'rgba(99, 102, 241, 0.1)'}
            >
              <Sparkles className="w-5 h-5" style={{ color: 'var(--brand-primary)' }} />
              <div className="text-left">
                <span className="block text-sm font-medium" style={{ color: 'var(--brand-primary)' }}>
                  Smart-Spalte erstellen
                </span>
                <span className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
                  Individuelle Filter mit UND/ODER Logik
                </span>
              </div>
            </button>
            
            {/* AI Column Button */}
            <button
              onClick={() => setShowAiColumnCreator(true)}
              className="flex items-center gap-2 w-full p-3 mb-2 rounded-lg transition-colors"
              style={{
                background: 'linear-gradient(135deg, rgba(168, 85, 247, 0.1), rgba(236, 72, 153, 0.1))',
                border: '1px dashed #a855f7',
              }}
              onMouseEnter={e => e.currentTarget.style.backgroundColor = 'rgba(168, 85, 247, 0.15)'}
              onMouseLeave={e => e.currentTarget.style.backgroundColor = 'rgba(168, 85, 247, 0.1)'}
            >
              <Bot className="w-5 h-5" style={{ color: '#a855f7' }} />
              <div className="text-left">
                <span className="block text-sm font-medium" style={{ color: '#a855f7' }}>
                  KI-Spalte erstellen
                </span>
                <span className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
                  AI analysiert E-Mails automatisch
                </span>
              </div>
            </button>
            
            {/* Meeting Prep Column Button */}
            <button
              onClick={() => {
                const account = accounts.find(a => a.id === selectedAccount);
                if (!account) {
                  toast.info('Bitte wähle zuerst einen Account aus');
                  return;
                }
                // Check if meeting prep column already exists
                if (columns.some(c => c.sourceType === 'meeting-prep')) {
                  toast.info('Meeting Prep Spalte existiert bereits');
                  return;
                }
                const newColumn: Column = {
                  id: `temp-${Date.now()}`,
                  sourceType: 'meeting-prep',
                  sourceId: 'meeting-prep',
                  mailAccountId: account.id,
                  displayName: 'Meeting Prep',
                  icon: '📅',
                  frozen: false,
                };
                setColumns([...columns, newColumn]);
                toast.success('Meeting Prep Spalte hinzugefügt');
              }}
              className="flex items-center gap-2 w-full p-3 mb-4 rounded-lg transition-colors"
              style={{
                background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(16, 185, 129, 0.1))',
                border: '1px dashed #3b82f6',
              }}
              onMouseEnter={e => e.currentTarget.style.backgroundColor = 'rgba(59, 130, 246, 0.15)'}
              onMouseLeave={e => e.currentTarget.style.backgroundColor = 'rgba(59, 130, 246, 0.1)'}
            >
              <Calendar className="w-5 h-5" style={{ color: '#3b82f6' }} />
              <div className="text-left">
                <span className="block text-sm font-medium" style={{ color: '#3b82f6' }}>
                  Meeting Prep Spalte
                </span>
                <span className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
                  Kalendertermine mit zugehörigen Mails
                </span>
              </div>
            </button>
            
            {/* Existing AI Columns */}
            {board?.smartAiColumns && board.smartAiColumns.length > 0 && (
              <div className="mb-4 space-y-2">
                <label className="block text-xs font-medium" style={{ color: 'var(--text-tertiary)' }}>
                  KI-Spalten ({board.smartAiColumns.length})
                </label>
                {board.smartAiColumns.map((aiCol) => (
                  <div
                    key={aiCol.id}
                    className="flex items-center justify-between p-3 rounded-lg"
                    style={{
                      backgroundColor: 'var(--bg-secondary)',
                      border: '1px solid var(--border-default)',
                    }}
                  >
                    <div className="flex items-center gap-2">
                      <span>{aiCol.icon || '🤖'}</span>
                      <span className="text-sm" style={{ color: 'var(--text-primary)' }}>{aiCol.name}</span>
                      {!aiCol.enabled && (
                        <span className="text-xs px-1.5 py-0.5 rounded" style={{ backgroundColor: 'var(--bg-tertiary)', color: 'var(--text-tertiary)' }}>
                          deaktiviert
                        </span>
                      )}
                    </div>
                    <button
                      onClick={async () => {
                        if (confirm(`KI-Spalte "${aiCol.name}" wirklich löschen?`)) {
                          try {
                            await aiApi.deleteAiColumn(aiCol.id);
                            toast.success('KI-Spalte gelöscht');
                            onSave(); // Refresh board
                          } catch {
                            toast.error('Fehler beim Löschen');
                          }
                        }
                      }}
                      className="p-1 rounded hover:bg-red-500/10 transition-colors"
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            
            {columns.length > 0 ? (
              <DndContext collisionDetection={closestCenter} onDragEnd={handleColumnDragEnd}>
                <SortableContext items={columns.map(c => c.id)} strategy={verticalListSortingStrategy}>
                  <div className="space-y-2 mb-4">
                    {columns.map((col, index) => (
                      <SortableColumnItem
                        key={col.id}
                        column={col}
                        index={index}
                        onToggleFrozen={() => handleToggleFrozen(index)}
                        onToggleHidden={() => handleToggleHidden(index)}
                        onToggleHideWhenEmpty={() => handleToggleHideWhenEmpty(index)}
                        onRemove={() => handleRemoveColumn(index)}
                        onChangeBaseFolder={(folderId) => {
                          setColumns(columns.map((c, i) => 
                            i === index ? { ...c, baseFolderId: folderId } : c
                          ));
                        }}
                        folders={folders}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            ) : (
              <p 
                className="text-sm mb-4"
                style={{ color: 'var(--text-tertiary)' }}
              >
                Noch keine Spalten. Wähle unten Ordner oder Labels aus.
              </p>
            )}
            
            {/* Add columns from folders */}
            {accounts.length > 0 && (
              <div 
                className="rounded-lg overflow-hidden"
                style={{ border: '1px solid var(--border-default)' }}
              >
                <div 
                  className="flex items-center gap-2 p-3"
                  style={{
                    backgroundColor: 'var(--bg-secondary)',
                    borderBottom: '1px solid var(--border-default)',
                  }}
                >
                  <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>Account:</span>
                  <select
                    value={selectedAccount || ''}
                    onChange={(e) => setSelectedAccount(e.target.value)}
                    className="flex-1 px-2 py-1 rounded text-sm"
                    style={{
                      backgroundColor: 'var(--bg-primary)',
                      border: '1px solid var(--border-default)',
                      color: 'var(--text-primary)',
                    }}
                  >
                    {accounts.map((account) => (
                      <option key={account.id} value={account.id}>
                        {account.providerEmail} ({account.provider})
                      </option>
                    ))}
                  </select>
                </div>
                
                {/* Punkt 2: Suchfeld */}
                <div 
                  className="p-2"
                  style={{ borderBottom: '1px solid var(--border-default)' }}
                >
                  <div className="relative">
                    <Search 
                      className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" 
                      style={{ color: 'var(--text-tertiary)' }}
                    />
                    <input
                      type="text"
                      value={folderSearch}
                      onChange={(e) => setFolderSearch(e.target.value)}
                      placeholder="Ordner/Kategorien suchen..."
                      className="w-full pl-9 pr-3 py-2 text-sm rounded-lg"
                      style={{
                        backgroundColor: 'var(--bg-primary)',
                        border: '1px solid var(--border-default)',
                        color: 'var(--text-primary)',
                      }}
                    />
                  </div>
                </div>
                
                <div className="max-h-72 overflow-y-auto">
                  {foldersLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-5 h-5 animate-spin text-primary" />
                    </div>
                  ) : filteredFolders.length === 0 ? (
                    <div className="flex items-center justify-center py-8">
                      <span className="text-sm" style={{ color: 'var(--text-tertiary)' }}>
                        {folderSearch ? 'Keine Ergebnisse' : 'Keine Ordner verfügbar'}
                      </span>
                    </div>
                  ) : (
                    <FolderTree
                      folders={filteredFolders}
                      selectedIds={columns.map(c => c.sourceId)}
                      onSelect={handleAddColumn}
                    />
                  )}
                </div>
              </div>
            )}
          </div>
          
          {/* Advanced Settings */}
          <div>
            <label 
              className="block text-sm font-medium mb-3"
              style={{ color: 'var(--text-secondary)' }}
            >
              Einstellungen
            </label>
            
            <label 
              className="flex items-start gap-3 p-3 rounded-lg cursor-pointer transition-colors"
              style={{
                backgroundColor: hideDuplicates ? 'rgba(99, 102, 241, 0.08)' : 'var(--bg-secondary)',
                border: `1px solid ${hideDuplicates ? 'var(--brand-primary)' : 'var(--border-default)'}`,
              }}
            >
              <input
                type="checkbox"
                checked={hideDuplicates}
                onChange={(e) => setHideDuplicates(e.target.checked)}
                className="mt-0.5 w-4 h-4 rounded border-2 accent-primary"
                style={{
                  accentColor: 'var(--brand-primary)',
                }}
              />
              <div className="flex-1">
                <span 
                  className="text-sm font-medium"
                  style={{ color: 'var(--text-primary)' }}
                >
                  Dopplungen vermeiden
                </span>
                <p 
                  className="text-xs mt-0.5"
                  style={{ color: 'var(--text-tertiary)' }}
                >
                  Wenn eine E-Mail in einem Ordner angezeigt wird und auch eine Kategorie hat, die auf diesem Board als Spalte existiert, wird sie nur in der Kategorie-Spalte angezeigt, nicht im Ordner.
                </p>
              </div>
            </label>
            
            {/* Show Completion Popup Setting */}
            <label 
              className="flex items-start gap-3 p-3 rounded-lg cursor-pointer transition-colors mt-2"
              style={{
                backgroundColor: showCompletionPopup ? 'rgba(99, 102, 241, 0.08)' : 'var(--bg-secondary)',
                border: `1px solid ${showCompletionPopup ? 'var(--brand-primary)' : 'var(--border-default)'}`,
              }}
            >
              <input
                type="checkbox"
                checked={showCompletionPopup}
                onChange={(e) => setShowCompletionPopup(e.target.checked)}
                className="mt-0.5 w-4 h-4 rounded border-2 accent-primary"
                style={{
                  accentColor: 'var(--brand-primary)',
                }}
              />
              <div className="flex-1">
                <span 
                  className="text-sm font-medium"
                  style={{ color: 'var(--text-primary)' }}
                >
                  Aktionspopup bei Aufgabenabschluss anzeigen
                </span>
                <p 
                  className="text-xs mt-0.5"
                  style={{ color: 'var(--text-tertiary)' }}
                >
                  Wenn du eine Hauptaufgabe (To-Do) als erledigt markierst, öffnet sich automatisch das Aktionen-Popup für weitere Optionen (z.B. E-Mail verschieben, Kategorie setzen).
                </p>
              </div>
            </label>
          </div>
        </div>
        
        {/* Footer */}
        <div 
          className="flex items-center justify-between px-6 py-4"
          style={{ borderTop: '1px solid var(--border-default)' }}
        >
          <div>
            {isEditing && board && !board.isDefault && (
              <button
                onClick={() => deleteMutation.mutate()}
                disabled={deleteMutation.isPending}
                className="px-3 py-2 text-sm rounded-lg transition-colors"
                style={{ color: 'var(--color-error)' }}
                onMouseEnter={e => e.currentTarget.style.backgroundColor = 'var(--color-error-bg)'}
                onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                Board löschen
              </button>
            )}
          </div>
          
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm rounded-lg transition-colors"
              style={{ color: 'var(--text-secondary)' }}
              onMouseEnter={e => e.currentTarget.style.backgroundColor = 'var(--bg-hover)'}
              onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              Abbrechen
            </button>
            <button
              onClick={handleSave}
              disabled={saveMutation.isPending || !name.trim()}
              className="px-4 py-2 text-sm text-white rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50"
              style={{
                background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
              }}
            >
              {saveMutation.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : isEditing ? (
                'Speichern'
              ) : (
                'Erstellen'
              )}
            </button>
          </div>
        </div>
      </div>
      
      {/* Smart Column Modal */}
      <SmartColumnModal
        isOpen={showSmartColumnModal}
        onClose={() => setShowSmartColumnModal(false)}
        onSave={handleAddSmartColumn}
        accounts={accounts}
        folders={folders}
        initialAccountId={selectedAccount || undefined}
      />
      
      {/* AI Column Creator Modal */}
      {showAiColumnCreator && board && (
        <SmartColumnCreator
          boardId={board.id}
          folders={(() => {
            // Flatten nested folders for AI column source selection
            const flattenFolders = (items: Folder[], prefix = ''): { id: string; name: string; type: 'folder' | 'category' | 'label' }[] => {
              return items.flatMap(f => {
                const displayName = prefix ? `${prefix} / ${f.displayName}` : f.displayName;
                const current = { id: f.id, name: displayName, type: f.type };
                const children = f.children ? flattenFolders(f.children, displayName) : [];
                return [current, ...children];
              });
            };
            return flattenFolders(folders);
          })()}
          mailAccountId={selectedAccount || undefined}
          onClose={() => setShowAiColumnCreator(false)}
          onCreate={async (input) => {
            await aiApi.createAiColumn(input);
            toast.success(`KI-Spalte "${input.name}" erstellt!`);
            setShowAiColumnCreator(false);
            onSave(); // Refresh board
          }}
        />
      )}
    </div>
  );
}
